import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

interface Pkg { id: string; name: string; tier_label: string | null; price_etb: number; banner_ad_quota: number; allows_banner_ads: boolean; }

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  inquiry: { id: string; package_id: string | null; full_name: string; email: string } | null;
  onApproved?: () => void;
}

export function ApproveInquiryDialog({ open, onOpenChange, inquiry, onApproved }: Props) {
  const qc = useQueryClient();
  const [packageId, setPackageId] = useState<string>('');
  const [duration, setDuration] = useState(30);
  const [amount, setAmount] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const { data: packages } = useQuery({
    queryKey: ['approve-packages-ref'],
    queryFn: async () => {
      const { data } = await supabase.from('advertising_packages' as any).select('id,name,tier_label,price_etb,banner_ad_quota,allows_banner_ads').eq('is_active', true);
      return (data as unknown as Pkg[]) || [];
    },
  });

  useEffect(() => {
    if (open) {
      setPackageId(inquiry?.package_id || '');
      setDuration(30);
      setAmount('');
    }
  }, [open, inquiry]);

  const selectedPkg = packages?.find((p) => p.id === packageId);

  const submit = async () => {
    if (!inquiry || !packageId) return toast.error('Pick a package');
    setSubmitting(true);
    const { data, error } = await supabase.rpc('approve_package_inquiry' as any, {
      _inquiry_id: inquiry.id,
      _package_id: packageId,
      _duration_days: duration,
      _amount_etb: amount ? Number(amount) : null,
      _user_id: null,
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success(`Approved — subscription ${(data as string)?.slice(0,8)}… created`);
    qc.invalidateQueries({ queryKey: ['package-inquiries'] });
    qc.invalidateQueries({ queryKey: ['pkg-funnel'] });
    onApproved?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Approve & provision package</DialogTitle>
          <DialogDescription>
            {inquiry && <>Lead: <strong>{inquiry.full_name}</strong> ({inquiry.email})</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Alert>
            <ShieldCheck className="h-4 w-4" />
            <AlertDescription className="text-xs">
              We will create or extend the user's subscription, mark the inquiry as won, and unlock their banner-ad quota.
            </AlertDescription>
          </Alert>
          <div>
            <Label>Package *</Label>
            <Select value={packageId} onValueChange={setPackageId}>
              <SelectTrigger><SelectValue placeholder="Select package" /></SelectTrigger>
              <SelectContent>
                {(packages || []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.tier_label || p.name} {p.allows_banner_ads ? `· ${p.banner_ad_quota} banners` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Duration (days)</Label>
              <Input type="number" min={1} value={duration} onChange={(e) => setDuration(parseInt(e.target.value || '30', 10))} />
            </div>
            <div>
              <Label>Amount (ETB)</Label>
              <Input type="number" min={0} placeholder={selectedPkg ? String(selectedPkg.price_etb) : 'Package price'} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !packageId}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Approve & provision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
